import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBackNavigation } from '@/hooks/use-back-navigation';
import { useIsMobile } from '@/hooks/use-mobile';
import { useSettings } from '@/hooks/useSettings';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Shield,
  Users,
  UserPlus,
  Plus,
  Trash2,
  Edit2,
  Search,
  KeyRound,
  BadgeCheck,
  UserCog,
  Lock,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { authenticatedFetch } from '@/lib/api';
import { useUserPrivileges } from '@/context/UserPrivilegesContext';
import { PRIVILEGE } from '@/lib/privilegeConstants';

// --- API types (privileges) ---
export interface ApiPrivilegeItem {
  PrevilageId: number;
  PrevilageName: string;
}

/** Normalize GET /api/GetAllPrevilagesInList response: either [] or { data: ApiPrivilegeItem[], ... } */
function normalizePrivilegesResponse(raw: unknown): ApiPrivilegeItem[] {
  if (Array.isArray(raw)) return raw as ApiPrivilegeItem[];
  if (raw && typeof raw === 'object' && 'data' in raw && Array.isArray((raw as { data: unknown }).data)) {
    return (raw as { data: ApiPrivilegeItem[] }).data;
  }
  return [];
}

// --- API types (GetUsers) ---
export interface ApiUserItem {
  Id?: number;
  UserId?: string;
  UserName?: string;
  Password?: string;
  LastLogin?: string | null;
  Roleid?: number;
  RoleName?: string;
  Active?: boolean;
  Location?: string;
  CompanyID?: number;
  CompanyName?: string;
  UID?: string | null;
  usertype?: number;
  SupplierID?: number;
  isCustomer?: boolean;
  pphone?: string;
}

/** Normalize GET /api/GetUsers response: either [] or { data: ApiUserItem[], ... } */
function normalizeGetUsersResponse(raw: unknown): ApiUserItem[] {
  if (Array.isArray(raw)) return raw as ApiUserItem[];
  if (raw && typeof raw === 'object' && 'data' in raw) {
    const d = (raw as { data: unknown }).data;
    if (Array.isArray(d)) return d as ApiUserItem[];
  }
  return [];
}

// --- API types (GetRoles) ---
export interface ApiRoleItem {
  RoleId: number;
  RoleName: string;
  RoleDesc?: string | null;
}

/** Normalize GET /api/GetRoles response: array or { data: [], totalRecords?: number } */
function normalizeGetRolesResponse(raw: unknown): { roles: ApiRoleItem[]; totalRecords: number } {
  let roles: ApiRoleItem[] = [];
  let totalRecords = 0;
  if (Array.isArray(raw)) {
    roles = raw as ApiRoleItem[];
    totalRecords = roles.length;
  } else if (raw && typeof raw === 'object' && 'data' in raw) {
    const obj = raw as { data: unknown; totalRecords?: number; total?: number };
    if (Array.isArray(obj.data)) {
      roles = obj.data as ApiRoleItem[];
      totalRecords = obj.totalRecords ?? obj.total ?? roles.length;
    }
  }
  return { roles, totalRecords };
}

// --- API types (GetRolePrevilagesById) ---
export interface ApiRolePrivilegeItem {
  Id?: number;
  RoleId?: number;
  RoleName?: string;
  PrevilageId: number;
  PrevilageName?: string;
}

/** Normalize GET /api/GetRolePrevilagesById response: either [] or { data: ApiRolePrivilegeItem[], ... } */
function normalizeGetRolePrivilegesByIdResponse(raw: unknown): ApiRolePrivilegeItem[] {
  if (Array.isArray(raw)) return raw as ApiRolePrivilegeItem[];
  if (raw && typeof raw === 'object' && 'data' in raw) {
    const d = (raw as { data: unknown }).data;
    if (Array.isArray(d)) return d as ApiRolePrivilegeItem[];
  }
  return [];
}

// --- Types ---
export interface Privilege {
  id: string;
  name: string;
  key: string;
  description?: string;
}

export interface Role {
  id: string;
  name: string;
  description?: string;
  privilegeIds: string[];
}

export interface User {
  id: string;
  name: string;
  email: string;
  username: string;
  roleId: string | null;
}

export interface UserManagementData {
  privileges: Privilege[];
  roles: Role[];
  users: User[];
}

const STORAGE_KEY = 'userManagementData';
const CURRENT_ROLE_KEY = 'userManagementCurrentRole';

const DEFAULT_ROLES: Role[] = [
  { id: 'super-admin', name: 'Super Admin', description: 'Full system access', privilegeIds: [] },
  { id: 'admin', name: 'Admin', description: 'Administrative access', privilegeIds: [] },
  { id: 'manager', name: 'Manager', description: 'Manage users and assignments', privilegeIds: [] },
  { id: 'staff', name: 'Staff', description: 'Limited access', privilegeIds: [] },
];

const ROLE_PERMISSIONS: Record<string, { canManagePrivileges: boolean; canManageRoles: boolean; canManageUsers: boolean }> = {
  'Super Admin': { canManagePrivileges: true, canManageRoles: true, canManageUsers: true },
  'Admin': { canManagePrivileges: true, canManageRoles: true, canManageUsers: true },
  'Manager': { canManagePrivileges: false, canManageRoles: false, canManageUsers: true },
  'Staff': { canManagePrivileges: false, canManageRoles: false, canManageUsers: false },
};

function loadData(): UserManagementData {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as UserManagementData;
      return {
        privileges: parsed.privileges ?? [],
        roles: (parsed.roles?.length ? parsed.roles : DEFAULT_ROLES) as Role[],
        users: parsed.users ?? [],
      };
    }
  } catch (_) {}
  return {
    privileges: [],
    roles: [...DEFAULT_ROLES],
    users: [],
  };
}

function saveData(data: UserManagementData) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

function getCurrentUserRole(): string {
  return localStorage.getItem(CURRENT_ROLE_KEY) || 'Super Admin';
}

function setCurrentUserRole(roleName: string) {
  localStorage.setItem(CURRENT_ROLE_KEY, roleName);
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

// --- Page ---
const UserManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPrivilege } = useUserPrivileges();
  const canUserCreate = hasPrivilege(PRIVILEGE.USER_CREATE);
  const canDeleteUser = hasPrivilege(PRIVILEGE.USER_DELETE);
  const canRoleCreate = hasPrivilege(PRIVILEGE.ROLE_CREATE);
  const canDeleteRole = hasPrivilege(PRIVILEGE.ROLE_DELETE);
  const [data, setData] = useState<UserManagementData>(loadData);
  const [currentRoleName, setCurrentRoleName] = useState(getCurrentUserRole);
  const [activeTab, setActiveTab] = useState('users');
  const [searchPrivileges, setSearchPrivileges] = useState('');
  const [searchRoles, setSearchRoles] = useState('');
  const [searchUsers, setSearchUsers] = useState('');

  // Privileges from API
  const [apiPrivileges, setApiPrivileges] = useState<ApiPrivilegeItem[]>([]);
  const [privilegesLoading, setPrivilegesLoading] = useState(true);
  const [privilegesError, setPrivilegesError] = useState<string | null>(null);

  // Roles from API
  const [apiRoles, setApiRoles] = useState<ApiRoleItem[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError] = useState<string | null>(null);
  const [rolesTotalRecords, setRolesTotalRecords] = useState(0);
  const isMobile = useIsMobile();
  const { primaryCompanyId } = useSettings();

  // Edit Role: loading existing privileges for the role (Check Privileges)
  const [rolePrivilegesLoading, setRolePrivilegesLoading] = useState(false);
  const [rolePrivilegesDialogOpen, setRolePrivilegesDialogOpen] = useState(false);
  const [rolePrivilegesList, setRolePrivilegesList] = useState<ApiRolePrivilegeItem[]>([]);

  // Modals
  const [privilegeModalOpen, setPrivilegeModalOpen] = useState(false);
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [editingPrivilege, setEditingPrivilege] = useState<Privilege | null>(null);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  /** When editing a user from the API list, store the API user for Id and field mapping */
  const [editingApiUser, setEditingApiUser] = useState<ApiUserItem | null>(null);

  // Form state
  const [privilegeForm, setPrivilegeForm] = useState({ name: '', key: '', description: '' });
  const [roleForm, setRoleForm] = useState({ name: '', description: '', privilegeIds: [] as string[] });
  const [userForm, setUserForm] = useState({
    userId: '',
    userName: '',
    password: '',
    email: '',
    username: '',
    roleId: '',
    locationId: '',
    companyId: '',
    phone: '',
  });
  const [submitting, setSubmitting] = useState(false);

  // Locations and companies for Add User dropdowns
  const [locations, setLocations] = useState<{ ID?: number; LocationID?: number; LocationId?: number; Name?: string }[]>([]);
  const [companies, setCompanies] = useState<{ Id?: number; CompanyID?: number; Name?: string }[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [companiesLoading, setCompaniesLoading] = useState(false);

  // Users from GET /api/GetUsers
  const [apiUsers, setApiUsers] = useState<ApiUserItem[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);

  // Mobile cards: lazy-load count and sentinel ref for Intersection Observer
  const USERS_PAGE_SIZE = 15;
  const ROLES_PAGE_SIZE = 15;
  const [usersVisibleCount, setUsersVisibleCount] = useState(USERS_PAGE_SIZE);
  const [rolesVisibleCount, setRolesVisibleCount] = useState(ROLES_PAGE_SIZE);
  const loadMoreUsersRef = useRef<HTMLDivElement>(null);
  const loadMoreRolesRef = useRef<HTMLDivElement>(null);

  const persist = useCallback((next: UserManagementData) => {
    setData(next);
    saveData(next);
  }, []);

  // Load privileges from API
  useEffect(() => {
    let cancelled = false;
    setPrivilegesLoading(true);
    setPrivilegesError(null);
    authenticatedFetch<unknown>('/api/GetAllPrevilagesInList')
      .then((res) => {
        if (cancelled) return;
        setApiPrivileges(normalizePrivilegesResponse(res.data));
      })
      .catch((err) => {
        if (cancelled) return;
        setPrivilegesError(err instanceof Error ? err.message : 'Failed to load privileges');
        setApiPrivileges([]);
        toast.error('Failed to load privileges');
      })
      .finally(() => {
        if (!cancelled) setPrivilegesLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Fetch roles from API (used by Roles tab and Users dropdown)
  const fetchRoles = useCallback(async (search: string, pageNumber: number, maxRecords: number) => {
    setRolesLoading(true);
    setRolesError(null);
    try {
      const searchParam = search.trim() ? encodeURIComponent(search.trim()) : '';
      const url = `/api/GetRoles?pageNumber=${pageNumber}&maxRecords=${maxRecords}&search=${searchParam}`;
      const res = await authenticatedFetch<unknown>(url);
      const { roles, totalRecords } = normalizeGetRolesResponse(res.data);
      setApiRoles(roles);
      setRolesTotalRecords(totalRecords);
    } catch (err) {
      setRolesError(err instanceof Error ? err.message : 'Failed to load roles');
      setApiRoles([]);
      setRolesTotalRecords(0);
      toast.error('Failed to load roles');
    } finally {
      setRolesLoading(false);
    }
  }, []);

  // Load roles when Roles or Users tab is active (all data; mobile uses lazy-rendered cards)
  useEffect(() => {
    if (activeTab !== 'roles' && activeTab !== 'users') return;
    const search = activeTab === 'roles' ? searchRoles : '';
    const t = setTimeout(() => {
      if (activeTab === 'roles') {
        fetchRoles(search, 0, 0);
      } else {
        fetchRoles(search, 0, 500);
      }
    }, activeTab === 'roles' ? 350 : 0);
    return () => clearTimeout(t);
  }, [activeTab, searchRoles, fetchRoles]);

  const permissions = ROLE_PERMISSIONS[currentRoleName] ?? ROLE_PERMISSIONS['Staff'];

  useEffect(() => {
    setCurrentUserRole(currentRoleName);
  }, [currentRoleName]);

  // Keep active tab valid when privileges/role change (Users tab = 46, Roles tab = 48, Privileges = role-based)
  useEffect(() => {
    const allowed = [
      canUserCreate && 'users',
      canRoleCreate && 'roles',
      permissions.canManagePrivileges && 'privileges',
    ].filter(Boolean) as string[];
    if (allowed.length && !allowed.includes(activeTab)) {
      setActiveTab(allowed[0]);
    }
  }, [currentRoleName, canUserCreate, canRoleCreate, permissions.canManagePrivileges, activeTab]);

  const handleClose = () => navigate('/?tab=administrator');

  // Back button (hardware/browser) should return to Administrator tab, not Reports
  useBackNavigation({
    onBack: handleClose,
    showExitConfirmation: false,
  });

  // --- Privileges ---
  const filteredPrivilegesFromApi = apiPrivileges.filter(
    (p) =>
      !searchPrivileges ||
      p.PrevilageName.toLowerCase().includes(searchPrivileges.toLowerCase())
  );

  const openAddPrivilege = () => {
    setEditingPrivilege(null);
    setPrivilegeForm({ name: '', key: '', description: '' });
    setPrivilegeModalOpen(true);
  };

  const openEditPrivilege = (p: Privilege) => {
    setEditingPrivilege(p);
    setPrivilegeForm({ name: p.name, key: p.key, description: p.description ?? '' });
    setPrivilegeModalOpen(true);
  };

  const savePrivilege = () => {
    const name = privilegeForm.name.trim();
    const key = privilegeForm.key.trim() || name.replace(/\s+/g, '_').toLowerCase();
    if (!name) {
      toast.error('Name is required');
      return;
    }
    setSubmitting(true);
    if (editingPrivilege) {
      const next = {
        ...data,
        privileges: data.privileges.map((p) =>
          p.id === editingPrivilege.id ? { ...p, name, key, description: privilegeForm.description || undefined } : p
        ),
      };
      persist(next);
      toast.success('Privilege updated');
    } else {
      const newP: Privilege = { id: generateId(), name, key, description: privilegeForm.description || undefined };
      persist({ ...data, privileges: [...data.privileges, newP] });
      toast.success('Privilege added');
    }
    setSubmitting(false);
    setPrivilegeModalOpen(false);
  };

  const deletePrivilege = (id: string) => {
    persist({
      ...data,
      privileges: data.privileges.filter((p) => p.id !== id),
      roles: data.roles.map((r) => ({ ...r, privilegeIds: r.privilegeIds.filter((pid) => pid !== id) })),
    });
    toast.success('Privilege removed');
  };

  // --- Roles ---
  /** Map API role to Role shape for Edit modal */
  const apiRoleToRole = (ar: ApiRoleItem): Role => ({
    id: String(ar.RoleId),
    name: ar.RoleName,
    description: ar.RoleDesc ?? undefined,
    privilegeIds: [],
  });

  const openAddRole = () => {
    setEditingRole(null);
    setRoleForm({ name: '', description: '', privilegeIds: [] });
    setRoleModalOpen(true);
  };

  const openEditRole = (r: Role) => {
    setEditingRole(r);
    setRoleForm({ name: r.name, description: r.description ?? '', privilegeIds: r.privilegeIds ?? [] });
    setRoleModalOpen(true);
  };

  const saveRole = async () => {
    const name = roleForm.name.trim();
    if (!name) {
      toast.error('Role name is required');
      return;
    }
    setSubmitting(true);
    if (editingRole) {
      const roleIdNum = parseInt(editingRole.id, 10);
      if (Number.isNaN(roleIdNum)) {
        toast.error('Invalid role ID');
        setSubmitting(false);
        return;
      }
      const preidList = roleForm.privilegeIds.map((id) => parseInt(id, 10)).filter((n) => !Number.isNaN(n));
      try {
        const res = await authenticatedFetch<{ status?: number; message?: string }>(
          '/api/AddRolePrevilage',
          {
            method: 'POST',
            body: JSON.stringify({ roleid: roleIdNum, preidList }),
          }
        );
        const payload = (res.data as { status?: number; message?: string }) ?? res;
        const status = payload?.status;
        const message = payload?.message ?? '';
        if (status === 0) {
          toast.success(message || 'Privileges added to role successfully.');
          const next = {
            ...data,
            roles: data.roles.map((r) =>
              r.id === editingRole.id
                ? { ...r, name, description: roleForm.description || undefined, privilegeIds: roleForm.privilegeIds }
                : r
            ),
          };
          persist(next);
          setRoleModalOpen(false);
          fetchRoles(searchRoles, 0, 0);
        } else {
          toast.error(message || 'Failed to add privileges to role.');
        }
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to add privileges to role.');
      } finally {
        setSubmitting(false);
      }
      return;
    }
    // Create role via API
    try {
      const preidList = roleForm.privilegeIds.map((id) => parseInt(id, 10)).filter((n) => !Number.isNaN(n));
      const body = {
        rolename: name,
        description: roleForm.description?.trim() ?? '',
        preidList,
      };
      const res = await authenticatedFetch<{ status?: number; message?: string; data?: unknown }>(
        '/api/AddRole',
        {
          method: 'POST',
          body: JSON.stringify(body),
        }
      );
      const payload = res.data;
      const status = payload?.status;
      const message = payload?.message ?? '';
      if (status === 0) {
        toast.success(message || 'Role created successfully.');
        setRoleModalOpen(false);
        const newR: Role = {
          id: generateId(),
          name,
          description: roleForm.description?.trim() || undefined,
          privilegeIds: roleForm.privilegeIds,
        };
        persist({ ...data, roles: [...data.roles, newR] });
        fetchRoles(searchRoles, 0, 0);
      } else {
        toast.error(message || 'Failed to create role.');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create role.');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteRole = async (id: string) => {
    const roleIdNum = parseInt(id, 10);
    if (Number.isNaN(roleIdNum)) {
      toast.error('Invalid role ID');
      return;
    }
    try {
      const res = await authenticatedFetch<{ status?: number; message?: string }>('/api/DeleteRole', {
        method: 'DELETE',
        body: JSON.stringify({ RoleId: roleIdNum }),
      });
      const payload = (res.data as { status?: number; message?: string }) ?? res;
      const status = payload?.status;
      const message = payload?.message ?? '';
      if (status === 0) {
        toast.success(message || 'Role deleted successfully.');
        persist({ ...data, roles: data.roles.filter((r) => r.id !== id) });
        fetchRoles(searchRoles, 0, 0);
      } else {
        toast.error(message || 'Failed to delete role.');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete role.');
    }
  };

  const toggleRolePrivilege = (privilegeId: string) => {
    setRoleForm((prev) => ({
      ...prev,
      privilegeIds: prev.privilegeIds.includes(privilegeId)
        ? prev.privilegeIds.filter((pid) => pid !== privilegeId)
        : [...prev.privilegeIds, privilegeId],
    }));
  };

  const fetchRolePrivileges = useCallback(async () => {
    if (!editingRole) return;
    const roleId = editingRole.id.trim();
    if (!roleId) return;
    setRolePrivilegesLoading(true);
    try {
      const res = await authenticatedFetch<unknown>(
        `/api/GetRolePrevilagesById?roleid=${encodeURIComponent(roleId)}`
      );
      const list = normalizeGetRolePrivilegesByIdResponse(res.data);
      const privilegeIds = list.map((item) => String(item.PrevilageId));
      setRoleForm((prev) => ({ ...prev, privilegeIds }));
      setRolePrivilegesList(list);
      setRolePrivilegesDialogOpen(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load role privileges.');
    } finally {
      setRolePrivilegesLoading(false);
    }
  }, [editingRole]);

  // --- Users ---
  const fetchUsers = useCallback(async (search: string) => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const query = search.trim() ? `?search=${encodeURIComponent(search.trim())}` : '?search=';
      const res = await authenticatedFetch<unknown>(`/api/GetUsers${query}`);
      setApiUsers(normalizeGetUsersResponse(res.data));
    } catch (err) {
      setUsersError(err instanceof Error ? err.message : 'Failed to load users');
      setApiUsers([]);
      toast.error('Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canUserCreate || activeTab !== 'users') return;
    fetchUsers(searchUsers);
  }, [activeTab, searchUsers, canUserCreate, fetchUsers]);

  // Reset lazy-load count when user list changes
  useEffect(() => {
    setUsersVisibleCount(USERS_PAGE_SIZE);
  }, [apiUsers.length]);

  // Lazy load more users when sentinel scrolls into view (mobile cards)
  useEffect(() => {
    const el = loadMoreUsersRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setUsersVisibleCount((prev) => Math.min(prev + USERS_PAGE_SIZE, apiUsers.length));
        }
      },
      { rootMargin: '100px', threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [apiUsers.length]);

  // Reset roles lazy-load count when role list changes
  useEffect(() => {
    setRolesVisibleCount(ROLES_PAGE_SIZE);
  }, [apiRoles.length]);

  // Lazy load more roles when sentinel scrolls into view (mobile cards)
  useEffect(() => {
    const el = loadMoreRolesRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setRolesVisibleCount((prev) => Math.min(prev + ROLES_PAGE_SIZE, apiRoles.length));
        }
      },
      { rootMargin: '100px', threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [apiRoles.length]);

  const fetchLocationsAndCompanies = useCallback(async () => {
    setLocationsLoading(true);
    setCompaniesLoading(true);
    try {
      const [locRes, compRes] = await Promise.all([
        authenticatedFetch<{ data?: { ID?: number; LocationID?: number; LocationId?: number; Name?: string }[] }>('/api/Locations'),
        authenticatedFetch<{ data?: { Id?: number; CompanyID?: number; Name?: string }[] }>('/api/Companies'),
      ]);
      const locData = locRes.data as any;
      const compData = compRes.data as any;
      setLocations(Array.isArray(locData) ? locData : locData?.data ?? []);
      setCompanies(Array.isArray(compData) ? compData : compData?.data ?? []);
    } catch (_) {
      setLocations([]);
      setCompanies([]);
    } finally {
      setLocationsLoading(false);
      setCompaniesLoading(false);
    }
  }, []);

  const openAddUser = () => {
    setEditingUser(null);
    setEditingApiUser(null);
    const adminRole = apiRoles.find((r) => r.RoleName === 'Admin');
    setUserForm({
      userId: '',
      userName: '',
      password: '',
      email: '',
      username: '',
      roleId: adminRole ? String(adminRole.RoleId) : '',
      locationId: '',
      companyId: primaryCompanyId || '',
      phone: '',
    });
    setUserModalOpen(true);
    fetchLocationsAndCompanies();
  };

  const openEditUser = (u: User, apiUser?: ApiUserItem) => {
    setEditingUser(u);
    setEditingApiUser(apiUser ?? null);
    setUserForm({
      userId: apiUser?.UserId ?? (u as any).userId ?? '',
      userName: u.name,
      password: '',
      email: u.email ?? '',
      username: u.username,
      roleId: u.roleId ?? '',
      locationId: apiUser?.Location ?? (u as any).locationId ?? '',
      companyId: apiUser?.CompanyID != null ? String(apiUser.CompanyID) : (u as any).companyId ?? '',
      phone: apiUser?.pphone ?? (u as any).phone ?? '',
    });
    setUserModalOpen(true);
    fetchLocationsAndCompanies();
  };

  const saveUser = async () => {
    const userName = userForm.userName.trim();
    if (!userName) {
      toast.error('User name is required');
      return;
    }
    const userId = userForm.userId.trim();
    if (!userId) {
      toast.error('User ID is required');
      return;
    }
    if (!editingUser) {
      if (!userForm.password) {
        toast.error('Password is required when creating a user');
        return;
      }
    }
    setSubmitting(true);
    try {
      if (editingUser) {
        const locationId = userForm.locationId.trim();
        const companyId = userForm.companyId.trim();
        const roleId = userForm.roleId.trim();
        if (!locationId || !companyId) {
          toast.error('Location and Company are required');
          setSubmitting(false);
          return;
        }
        const projectValue = locations.find((loc) => String(loc.ID ?? loc.LocationID ?? loc.LocationId ?? '') === locationId)?.Name ?? locationId;
        const idNum = editingApiUser?.Id ?? parseInt(editingUser.id, 10);
        if (Number.isNaN(idNum)) {
          toast.error('Invalid user ID');
          setSubmitting(false);
          return;
        }
        const body = {
          Id: idNum,
          user_id: userId,
          user_name: userForm.userName.trim(),
          password: userForm.password,
          email_id: userForm.email.trim() || undefined,
          project: projectValue,
          companyid: companyId,
          roleid: roleId ? parseInt(roleId, 10) : 0,
          supplierid: '',
          phone: userForm.phone.trim() || undefined,
        };
        console.log('UpdateUser PUT body:', JSON.stringify(body, null, 2));
        const res = await authenticatedFetch<{
          status?: number;
          message?: string;
          data?: unknown;
        }>('/api/UpdateUser', {
          method: 'PUT',
          body: JSON.stringify(body),
        });
        const payload = (res.data as { status?: number; message?: string }) || (res as { status?: number; message?: string });
        const status = payload.status;
        const message = payload.message || '';
        if (status === 0 || (typeof status === 'undefined' && message.toLowerCase().includes('success'))) {
          toast.success(message || 'User updated successfully');
          setUserModalOpen(false);
          fetchUsers(searchUsers);
        } else {
          toast.error(message || 'Failed to update user');
        }
      } else {
        const locationId = userForm.locationId.trim();
        const companyId = userForm.companyId.trim();
        const roleId = userForm.roleId.trim();
        if (!locationId || !companyId) {
          toast.error('Location and Company are required');
          setSubmitting(false);
          return;
        }
        const projectValue = locations.find((loc) => String(loc.ID ?? loc.LocationID ?? loc.LocationId ?? '') === locationId)?.Name ?? locationId;
        const body = {
          user_id: userForm.userId.trim(),
          user_name: userForm.userName.trim(),
          password: userForm.password,
          email_id: userForm.email.trim() || undefined,
          project: projectValue,
          companyid: companyId,
          roleid: roleId ? parseInt(roleId, 10) : 0,
          supplierid: '',
          phone: userForm.phone.trim() || undefined,
        };
        console.log('AddUser POST body:', JSON.stringify(body, null, 2));
        const res = await authenticatedFetch<{
          status?: number;
          message?: string;
          data?: unknown;
        }>('/api/AddUser', {
          method: 'POST',
          body: JSON.stringify(body),
        });
        const payload = (res.data as { status?: number; message?: string }) || res as { status?: number; message?: string };
        const status = payload.status;
        const message = payload.message || '';
        if (status === 0 || (typeof status === 'undefined' && message.toLowerCase().includes('success'))) {
          toast.success(message || 'User added successfully');
          const newU: User = {
            id: generateId(),
            name: userForm.userName,
            email: userForm.email.trim(),
            username: userForm.userId.trim() || userForm.userName,
            roleId: userForm.roleId || null,
          };
          persist({ ...data, users: [...data.users, newU] });
          setUserModalOpen(false);
          fetchUsers(searchUsers);
        } else {
          toast.error(message || 'Failed to add user');
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save user';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const deleteUser = async (id: string) => {
    const idNum = parseInt(id, 10);
    if (Number.isNaN(idNum)) {
      toast.error('Invalid user ID');
      return;
    }
    try {
      const res = await authenticatedFetch<{ status?: number; message?: string }>('/api/DeleteUser', {
        method: 'DELETE',
        body: JSON.stringify({ Id: idNum }),
      });
      const payload = (res.data as { status?: number; message?: string }) || (res as { status?: number; message?: string });
      const status = payload.status;
      const message = payload.message || '';
      if (status === 0 || (typeof status === 'undefined' && message.toLowerCase().includes('success'))) {
        toast.success(message || 'User deleted successfully.');
        persist({ ...data, users: data.users.filter((u) => u.id !== id) });
        fetchUsers(searchUsers);
      } else {
        toast.error(message || 'Failed to delete user');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete user');
    }
  };

  const getRoleName = (roleId: string | null) => {
    if (!roleId) return '—';
    const idNum = parseInt(roleId, 10);
    if (!Number.isNaN(idNum)) {
      const fromApi = apiRoles.find((r) => r.RoleId === idNum)?.RoleName;
      if (fromApi) return fromApi;
    }
    const fromLocal = data.roles.find((r) => r.id === roleId)?.name;
    if (fromLocal) return fromLocal;
    return '—';
  };

  /** Map API user to User shape for Edit modal */
  const apiUserToUser = (au: ApiUserItem): User => ({
    id: String(au.Id ?? ''),
    name: au.UserName ?? '',
    email: '',
    username: au.UserId ?? au.UserName ?? '',
    roleId: au.Roleid != null ? String(au.Roleid) : null,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.05 },
    },
    exit: { opacity: 0 },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gradient-to-br from-slate-50 via-white to-indigo-50/30">
      {/* Decorative background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-indigo-200/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 -left-40 w-72 h-72 bg-violet-200/15 rounded-full blur-3xl" />
      </div>

      <div className="relative flex-1 min-h-0 flex flex-col max-w-6xl mx-auto w-full p-4 md:p-6 lg:p-8 overflow-hidden">
        {/* Header - fixed */}
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex-shrink-0 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 md:mb-6"
        >
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-foreground hover:bg-white/80 rounded-xl"
              onClick={handleClose}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Administrator
            </Button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="flex-shrink-0 mb-4 md:mb-6"
        >
          <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
            User Management
          </h1>
          <p className="text-muted-foreground mt-1 text-xs sm:text-sm">
            Manage privileges, roles, users, and assignments. Visibility depends on your role.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className={cn(
            'flex-1 min-h-0 flex flex-col rounded-2xl border border-white/60 bg-white/70 backdrop-blur-xl shadow-xl shadow-slate-200/50 overflow-hidden'
          )}
        >
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 min-h-0 flex flex-col overflow-hidden w-full">
            <div className="flex-shrink-0 border-b border-slate-200/60 bg-white/50 px-2 md:px-4">
              <TabsList className="w-full h-auto flex flex-nowrap gap-1 bg-transparent p-0 rounded-none border-0 overflow-x-auto min-w-0">
                {canUserCreate && (
                  <TabsTrigger
                    value="users"
                    className="rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm px-2 py-2 sm:px-4 sm:py-3 gap-1 sm:gap-2 text-xs sm:text-sm shrink-0"
                  >
                    <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    Users
                  </TabsTrigger>
                )}
                {canRoleCreate && (
                  <TabsTrigger
                    value="roles"
                    className="rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm px-2 py-2 sm:px-4 sm:py-3 gap-1 sm:gap-2 text-xs sm:text-sm shrink-0"
                  >
                    <Shield className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    Roles
                  </TabsTrigger>
                )}
                {permissions.canManagePrivileges && (
                  <TabsTrigger
                    value="privileges"
                    className="rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm px-2 py-2 sm:px-4 sm:py-3 gap-1 sm:gap-2 text-xs sm:text-sm shrink-0"
                  >
                    <KeyRound className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    Privileges
                  </TabsTrigger>
                )}
              </TabsList>
            </div>

            <div className="flex-1 min-h-0 flex flex-col overflow-hidden p-4 md:p-6">
              <AnimatePresence mode="wait">
                {canUserCreate && activeTab === 'users' && (
                  <TabsContent value="users" forceMount className="mt-0 flex-1 min-h-0 flex flex-col overflow-hidden focus-visible:outline-none data-[state=inactive]:hidden">
                    <motion.div
                      variants={containerVariants}
                      initial="hidden"
                      animate="visible"
                      className="flex-1 min-h-0 flex flex-col overflow-hidden"
                    >
                      <div className="flex-shrink-0 flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4">
                        <div className="relative flex-1 max-w-sm">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground" />
                          <Input
                            placeholder="Search users..."
                            value={searchUsers}
                            onChange={(e) => setSearchUsers(e.target.value)}
                            className="pl-8 sm:pl-9 rounded-xl bg-white/80 border-slate-200/80 text-xs sm:text-sm h-8 sm:h-10"
                          />
                        </div>
                        <Button
                          onClick={openAddUser}
                          className="rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white shadow-lg shadow-indigo-200/50 text-xs sm:text-sm h-8 sm:h-10 px-3 sm:px-4"
                        >
                          <UserPlus className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                          Create User
                        </Button>
                      </div>
                      <div className="flex-1 min-h-0 overflow-auto overflow-x-auto rounded-xl border border-slate-200/60 bg-white/50 touch-pan-y overscroll-contain [-webkit-overflow-scrolling:touch]">
                        {usersLoading ? (
                          <div className="flex items-center justify-center py-16">
                            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                          </div>
                        ) : usersError ? (
                          <div className="py-12 text-center text-destructive">
                            {usersError}
                          </div>
                        ) : (
                          <>
                            {/* Mobile: scrollable cards with lazy loading */}
                            <div className="block md:hidden p-3 space-y-3">
                              {apiUsers.slice(0, usersVisibleCount).map((u) => (
                                <motion.div
                                  key={String(u.Id ?? u.UserId ?? '')}
                                  variants={itemVariants}
                                  className="rounded-xl border border-slate-200/80 bg-white p-3 shadow-sm"
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                      <p className="font-semibold text-slate-800 truncate text-xs">{u.UserName ?? '—'}</p>
                                      <p className="text-[10px] text-muted-foreground font-mono truncate mt-0.5">
                                        {u.UserId ?? u.UserName ?? '—'}
                                      </p>
                                      <p className="text-[10px] text-muted-foreground mt-0.5">
                                        {u.RoleName ?? '—'}
                                      </p>
                                      {(u.Location ?? '').trim() ? (
                                        <p className="text-[10px] text-muted-foreground mt-0.5 truncate">Location: {u.Location}</p>
                                      ) : null}
                                      {(u.CompanyName ?? '').trim() ? (
                                        <p className="text-[10px] text-muted-foreground mt-0.5 truncate">Company: {u.CompanyName}</p>
                                      ) : null}
                                      {(u.pphone ?? '').trim() ? (
                                        <p className="text-[10px] text-muted-foreground mt-0.5 truncate">{u.pphone}</p>
                                      ) : null}
                                    </div>
                                    <div className="flex shrink-0 gap-0.5 flex-row">
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7 rounded-lg"
                                        onClick={() => openEditUser(apiUserToUser(u), u)}
                                      >
                                        <Edit2 className="h-3 w-3" />
                                      </Button>
                                      {canDeleteUser && (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7 rounded-lg text-destructive hover:text-destructive"
                                        onClick={() => deleteUser(String(u.Id ?? ''))}
                                      >
                                        <Trash2 className="h-3 w-3" />
                                      </Button>
                                      )}
                                    </div>
                                  </div>
                                </motion.div>
                              ))}
                              {/* Sentinel for lazy load: when this is visible, load more */}
                              {apiUsers.length > usersVisibleCount && (
                                <div ref={loadMoreUsersRef} className="py-4 flex justify-center">
                                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                </div>
                              )}
                            </div>
                            {/* Desktop: table (all users) */}
                            <div className="hidden md:block">
                              <Table>
                                <TableHeader>
                                  <TableRow className="border-slate-200/60 hover:bg-transparent sticky top-0 z-10 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 border-b border-slate-200/60">
                                    <TableHead className="rounded-tl-xl">Name</TableHead>
                                    <TableHead>Username</TableHead>
                                    <TableHead className="hidden md:table-cell">Phone</TableHead>
                                    <TableHead>Role</TableHead>
                                    <TableHead>Location</TableHead>
                                    <TableHead>Company Name</TableHead>
                                    <TableHead className="w-[100px] rounded-tr-xl text-right">Actions</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {apiUsers.map((u) => (
                                    <motion.tr
                                      key={String(u.Id ?? u.UserId ?? '')}
                                      variants={itemVariants}
                                      className="border-b border-slate-100 hover:bg-slate-50/80"
                                    >
                                      <TableCell className="font-medium">{u.UserName ?? '—'}</TableCell>
                                      <TableCell className="font-mono text-sm">{u.UserId ?? u.UserName ?? '—'}</TableCell>
                                      <TableCell className="hidden md:table-cell text-muted-foreground text-sm">
                                        {u.pphone ?? '—'}
                                      </TableCell>
                                      <TableCell className="text-sm text-muted-foreground">
                                        {u.RoleName ?? '—'}
                                      </TableCell>
                                      <TableCell className="text-sm text-muted-foreground">
                                        {u.Location ?? '—'}
                                      </TableCell>
                                      <TableCell className="text-sm text-muted-foreground">
                                        {u.CompanyName ?? '—'}
                                      </TableCell>
                                      <TableCell className="text-right">
                                        <div className="flex justify-end gap-1">
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8 rounded-lg"
                                            onClick={() => openEditUser(apiUserToUser(u), u)}
                                          >
                                            <Edit2 className="h-3.5 w-3.5" />
                                          </Button>
                                          {canDeleteUser && (
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8 rounded-lg text-destructive hover:text-destructive"
                                            onClick={() => deleteUser(String(u.Id ?? ''))}
                                          >
                                            <Trash2 className="h-3.5 w-3.5" />
                                          </Button>
                                          )}
                                        </div>
                                      </TableCell>
                                    </motion.tr>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          </>
                        )}
                        {!usersLoading && !usersError && apiUsers.length === 0 && (
                          <div className="py-12 text-center text-muted-foreground">
                            No users yet. Create one to get started.
                          </div>
                        )}
                      </div>
                    </motion.div>
                  </TabsContent>
                )}

                {canRoleCreate && activeTab === 'roles' && (
                  <TabsContent value="roles" forceMount className="mt-0 flex-1 min-h-0 flex flex-col overflow-hidden focus-visible:outline-none data-[state=inactive]:hidden">
                    <motion.div
                      variants={containerVariants}
                      initial="hidden"
                      animate="visible"
                      className="flex-1 min-h-0 flex flex-col overflow-hidden"
                    >
                      <div className="flex-shrink-0 flex flex-col sm:flex-row gap-3 mb-4">
                        <div className="relative flex-1 max-w-sm">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            placeholder="Search roles..."
                            value={searchRoles}
                            onChange={(e) => setSearchRoles(e.target.value)}
                            className="pl-9 rounded-xl bg-white/80 border-slate-200/80"
                          />
                        </div>
                        <Button
                          onClick={openAddRole}
                          className="rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white shadow-lg shadow-indigo-200/50"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Create Role
                        </Button>
                      </div>
                      {/* Mobile: total records */}
                      <div className="block md:hidden flex-shrink-0 text-xs text-muted-foreground mb-2">
                        Total: {rolesTotalRecords} record{rolesTotalRecords !== 1 ? 's' : ''}
                      </div>
                      <div className="flex-1 min-h-0 overflow-auto overflow-x-auto rounded-xl border border-slate-200/60 bg-white/50 touch-pan-y overscroll-contain [-webkit-overflow-scrolling:touch]">
                        {rolesLoading ? (
                          <div className="flex items-center justify-center py-16">
                            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                          </div>
                        ) : rolesError ? (
                          <div className="py-12 text-center text-destructive">
                            {rolesError}
                          </div>
                        ) : apiRoles.length === 0 ? (
                          <div className="py-12 text-center text-muted-foreground">
                            No roles found.
                          </div>
                        ) : (
                          <>
                            {/* Mobile: scrollable role cards with lazy loading */}
                            <div className="block md:hidden p-3 space-y-3">
                              {apiRoles.slice(0, rolesVisibleCount).map((r) => (
                                <motion.div
                                  key={r.RoleId}
                                  variants={itemVariants}
                                  className="rounded-xl border border-slate-200/80 bg-white p-3 shadow-sm"
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                      <p className="font-semibold text-slate-800 truncate text-xs">{r.RoleName}</p>
                                      <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-2">
                                        {r.RoleDesc || '—'}
                                      </p>
                                    </div>
                                    <div className="flex shrink-0 gap-0.5 flex-row">
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7 rounded-lg"
                                        onClick={() => openEditRole(apiRoleToRole(r))}
                                      >
                                        <Edit2 className="h-3 w-3" />
                                      </Button>
                                      {canDeleteRole && (
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-7 w-7 rounded-lg text-destructive hover:text-destructive"
                                          onClick={() => deleteRole(String(r.RoleId))}
                                        >
                                          <Trash2 className="h-3 w-3" />
                                        </Button>
                                        )}
                                    </div>
                                  </div>
                                </motion.div>
                              ))}
                              {apiRoles.length > rolesVisibleCount && (
                                <div ref={loadMoreRolesRef} className="py-4 flex justify-center">
                                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                </div>
                              )}
                            </div>
                            {/* Desktop: table (all roles) */}
                            <div className="hidden md:block">
                              <Table>
                                <TableHeader>
                                  <TableRow className="border-slate-200/60 hover:bg-transparent sticky top-0 z-10 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 border-b border-slate-200/60">
                                    <TableHead className="rounded-tl-xl">Role</TableHead>
                                    <TableHead className="rounded-tr-xl">Description</TableHead>
                                    <TableHead className="w-[100px] text-right">Actions</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {apiRoles.map((r) => (
                                    <motion.tr
                                      key={r.RoleId}
                                      variants={itemVariants}
                                      className="border-b border-slate-100 hover:bg-slate-50/80"
                                    >
                                      <TableCell className="font-medium">{r.RoleName}</TableCell>
                                      <TableCell className="text-muted-foreground text-sm max-w-[200px] truncate">
                                        {r.RoleDesc || '—'}
                                      </TableCell>
                                      <TableCell className="text-right">
                                        <div className="flex justify-end gap-1">
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8 rounded-lg"
                                            onClick={() => openEditRole(apiRoleToRole(r))}
                                          >
                                            <Edit2 className="h-3.5 w-3.5" />
                                          </Button>
                                          {canDeleteRole && (
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8 rounded-lg text-destructive hover:text-destructive"
                                            onClick={() => deleteRole(String(r.RoleId))}
                                          >
                                            <Trash2 className="h-3.5 w-3.5" />
                                          </Button>
                                          )}
                                        </div>
                                      </TableCell>
                                    </motion.tr>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          </>
                        )}
                      </div>
                    </motion.div>
                  </TabsContent>
                )}

                {permissions.canManagePrivileges && activeTab === 'privileges' && (
                  <TabsContent value="privileges" forceMount className="mt-0 flex-1 min-h-0 flex flex-col overflow-hidden focus-visible:outline-none data-[state=inactive]:hidden">
                    <motion.div
                      variants={containerVariants}
                      initial="hidden"
                      animate="visible"
                      className="flex-1 min-h-0 flex flex-col overflow-hidden"
                    >
                      <div className="flex-shrink-0 flex flex-col sm:flex-row gap-3 mb-4">
                        <div className="relative flex-1 max-w-sm">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            placeholder="Search privileges..."
                            value={searchPrivileges}
                            onChange={(e) => setSearchPrivileges(e.target.value)}
                            className="pl-9 rounded-xl bg-white/80 border-slate-200/80"
                          />
                        </div>
                      </div>
                      <div className="flex-1 min-h-0 overflow-auto rounded-xl border border-slate-200/60 bg-white/50">
                        {privilegesLoading ? (
                          <div className="flex items-center justify-center py-16">
                            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                          </div>
                        ) : privilegesError ? (
                          <div className="py-12 text-center text-destructive">
                            {privilegesError}
                          </div>
                        ) : filteredPrivilegesFromApi.length === 0 ? (
                          <div className="py-12 text-center text-muted-foreground">
                            {apiPrivileges.length === 0 ? 'No privileges found.' : 'No matching privileges.'}
                          </div>
                        ) : (
                          <Table>
                            <TableHeader>
                              <TableRow className="border-slate-200/60 hover:bg-transparent sticky top-0 z-10 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 border-b border-slate-200/60">
                                <TableHead className="rounded-tl-xl rounded-tr-xl">Name</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {filteredPrivilegesFromApi.map((p) => (
                                <motion.tr
                                  key={p.PrevilageId}
                                  variants={itemVariants}
                                  className="border-b border-slate-100 hover:bg-slate-50/80"
                                >
                                  <TableCell className="font-medium">{p.PrevilageName}</TableCell>
                                </motion.tr>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </div>
                    </motion.div>
                  </TabsContent>
                )}

                {!permissions.canManagePrivileges && !canRoleCreate && !canUserCreate && (
                  <div className="py-16 text-center">
                    <Lock className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">You don’t have access to any User Management sections with the current role.</p>
                  </div>
                )}
              </AnimatePresence>
            </div>
          </Tabs>
        </motion.div>
      </div>

      {/* Add/Edit Privilege Modal */}
      <Dialog open={privilegeModalOpen} onOpenChange={setPrivilegeModalOpen}>
        <DialogContent className="rounded-2xl border-slate-200/80 bg-white/95 backdrop-blur-xl shadow-2xl max-w-md">
          <DialogHeader>
            <DialogTitle>{editingPrivilege ? 'Edit Privilege' : 'Add Privilege'}</DialogTitle>
            <DialogDescription>Define a permission that can be assigned to roles.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Name</Label>
              <Input
                value={privilegeForm.name}
                onChange={(e) => setPrivilegeForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Manage Reports"
                className="rounded-xl"
              />
            </div>
            <div className="grid gap-2">
              <Label>Key (optional, auto-generated from name)</Label>
              <Input
                value={privilegeForm.key}
                onChange={(e) => setPrivilegeForm((p) => ({ ...p, key: e.target.value }))}
                placeholder="e.g. manage_reports"
                className="rounded-xl font-mono"
              />
            </div>
            <div className="grid gap-2">
              <Label>Description (optional)</Label>
              <Input
                value={privilegeForm.description}
                onChange={(e) => setPrivilegeForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="Short description"
                className="rounded-xl"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPrivilegeModalOpen(false)} className="rounded-xl">
              Cancel
            </Button>
            <Button onClick={savePrivilege} disabled={submitting} className="rounded-xl bg-indigo-600 hover:bg-indigo-700">
              {editingPrivilege ? 'Update' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Role Modal */}
      <Dialog open={roleModalOpen} onOpenChange={setRoleModalOpen}>
        <DialogContent className="rounded-2xl border-slate-200/80 bg-white/95 backdrop-blur-xl shadow-2xl max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{editingRole ? 'Edit Role' : 'Create Role'}</DialogTitle>
            <DialogDescription>Create a role and assign privileges to it.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4 overflow-y-auto">
            <div className="grid gap-2">
              <Label>Role name</Label>
              <Input
                value={roleForm.name}
                onChange={(e) => setRoleForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Manager"
                className="rounded-xl"
              />
            </div>
            <div className="grid gap-2">
              <Label>Description (optional)</Label>
              <Input
                value={roleForm.description}
                onChange={(e) => setRoleForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="Short description"
                className="rounded-xl"
              />
            </div>
            <div className="grid gap-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label>Privileges</Label>
                {editingRole && canRoleCreate && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-xl gap-2"
                    onClick={fetchRolePrivileges}
                    disabled={rolePrivilegesLoading}
                  >
                    {rolePrivilegesLoading && <Loader2 className="h-4 w-4 animate-spin shrink-0" />}
                    Check Privileges
                  </Button>
                )}
              </div>
              {!privilegesLoading && apiPrivileges.length > 0 && (
                <div className="flex items-center space-x-2 pb-2">
                  <Checkbox
                    id="role-priv-select-all"
                    checked={roleForm.privilegeIds.length === apiPrivileges.length}
                    onCheckedChange={(checked) => {
                      setRoleForm((prev) => ({
                        ...prev,
                        privilegeIds: checked ? apiPrivileges.map((p) => String(p.PrevilageId)) : [],
                      }));
                    }}
                  />
                  <label
                    htmlFor="role-priv-select-all"
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    Select all
                  </label>
                </div>
              )}
              <ScrollArea className="h-[200px] rounded-xl border border-slate-200/80 p-3 bg-slate-50/50">
                <div className="space-y-2">
                  {privilegesLoading ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                      Loading privileges...
                    </div>
                  ) : apiPrivileges.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      {privilegesError ? 'Could not load privileges.' : 'No privileges found.'}
                    </p>
                  ) : (
                    apiPrivileges.map((p) => {
                      const privilegeIdStr = String(p.PrevilageId);
                      return (
                        <div key={p.PrevilageId} className="flex items-center space-x-2">
                          <Checkbox
                            id={`role-priv-${p.PrevilageId}`}
                            checked={roleForm.privilegeIds.includes(privilegeIdStr)}
                            onCheckedChange={() => toggleRolePrivilege(privilegeIdStr)}
                          />
                          <label
                            htmlFor={`role-priv-${p.PrevilageId}`}
                            className="text-sm font-medium leading-none cursor-pointer"
                          >
                            {p.PrevilageName}
                          </label>
                        </div>
                      );
                    })
                  )}
                </div>
              </ScrollArea>
            </div>
          </div>
          <DialogFooter className="gap-2 border-t pt-4">
            <Button variant="outline" onClick={() => setRoleModalOpen(false)} className="rounded-xl">
              Cancel
            </Button>
            <Button onClick={saveRole} disabled={submitting} className="rounded-xl bg-indigo-600 hover:bg-indigo-700">
              {editingRole ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role privileges dialog (opened by "Check Privileges") */}
      <Dialog open={rolePrivilegesDialogOpen} onOpenChange={setRolePrivilegesDialogOpen}>
        <DialogContent className="rounded-2xl border-slate-200/80 bg-white/95 backdrop-blur-xl shadow-2xl max-w-md max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Privileges for {editingRole?.name ?? 'this role'}</DialogTitle>
            <DialogDescription>
              {rolePrivilegesList.length > 0
                ? `${rolePrivilegesList.length} privilege(s) assigned to this role.`
                : 'No privileges assigned to this role.'}
            </DialogDescription>
          </DialogHeader>
          <div
            className="h-[50vh] min-h-[240px] max-h-[50vh] overflow-y-auto overflow-x-hidden rounded-xl border border-slate-200/80 bg-slate-50/50 touch-pan-y overscroll-contain [-webkit-overflow-scrolling:touch]"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className="p-3">
              {rolePrivilegesList.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No privileges found.</p>
              ) : (
                <ul className="space-y-2">
                  {rolePrivilegesList.map((item) => (
                    <li
                      key={item.Id ?? item.PrevilageId}
                      className="flex items-center justify-between gap-2 py-1.5 px-2 rounded-lg bg-white/80 border border-slate-100 text-sm"
                    >
                      <span className="font-medium">{item.PrevilageName ?? '—'}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2 border-t pt-4">
            <Button
              variant="outline"
              onClick={() => setRolePrivilegesDialogOpen(false)}
              className="rounded-xl"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit User Modal */}
      <Dialog open={userModalOpen} onOpenChange={setUserModalOpen}>
        <DialogContent className="rounded-2xl border-slate-200/80 bg-white/95 backdrop-blur-xl shadow-2xl max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Update User' : 'Create User'}</DialogTitle>
            <DialogDescription>
              {editingUser ? 'Update the staff member details below.' : 'Add a staff member and optionally assign a role.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>User ID</Label>
              <Input
                value={userForm.userId}
                onChange={(e) => setUserForm((p) => ({ ...p, userId: e.target.value }))}
                placeholder="e.g. n123"
                className="rounded-xl font-mono"
              />
            </div>
            <div className="grid gap-2">
              <Label>User Name</Label>
              <Input
                value={userForm.userName}
                onChange={(e) => setUserForm((p) => ({ ...p, userName: e.target.value }))}
                placeholder="e.g. John Doe"
                className="rounded-xl"
              />
            </div>
            <div className="grid gap-2">
              <Label>Password{editingUser ? ' (leave blank to keep existing)' : ''}</Label>
              <Input
                type="password"
                value={userForm.password}
                onChange={(e) => setUserForm((p) => ({ ...p, password: e.target.value }))}
                placeholder={editingUser ? 'Leave blank to keep existing' : 'Enter password'}
                className="rounded-xl font-mono"
              />
            </div>
            <div className="grid gap-2 hidden">
              <Label>Email ID (optional)</Label>
              <Input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm((p) => ({ ...p, email: e.target.value }))}
                placeholder="e.g. john@company.com"
                className="rounded-xl"
              />
            </div>
            <div className="grid gap-2">
              <Label>Location</Label>
              <Select
                value={userForm.locationId || 'none'}
                onValueChange={(v) => setUserForm((p) => ({ ...p, locationId: v === 'none' ? '' : v }))}
                disabled={locationsLoading}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder={locationsLoading ? 'Loading...' : 'Select location'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select location</SelectItem>
                  {locations.map((loc) => {
                    const id = String(loc.ID ?? loc.LocationID ?? loc.LocationId ?? '');
                    return (
                      <SelectItem key={id} value={id}>
                        {loc.Name ?? `Location ${id}`}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Company</Label>
              <Select
                value={userForm.companyId || 'none'}
                onValueChange={(v) => setUserForm((p) => ({ ...p, companyId: v === 'none' ? '' : v }))}
                disabled={companiesLoading}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder={companiesLoading ? 'Loading...' : 'Select company'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select company</SelectItem>
                  {companies.map((comp) => {
                    const id = String(comp.Id ?? comp.CompanyID ?? '');
                    return (
                      <SelectItem key={id} value={id}>
                        {comp.Name ?? `Company ${id}`}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Phone Number (optional)</Label>
              <Input
                value={userForm.phone}
                onChange={(e) => setUserForm((p) => ({ ...p, phone: e.target.value }))}
                placeholder="e.g. 9988776655"
                className="rounded-xl"
              />
            </div>
            <div className="grid gap-2">
              <Label>Role</Label>
              <Select value={userForm.roleId || ''} onValueChange={(v) => setUserForm((p) => ({ ...p, roleId: v }))}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {apiRoles.map((r) => (
                    <SelectItem key={r.RoleId} value={String(r.RoleId)}>
                      {r.RoleName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setUserModalOpen(false)} className="rounded-xl">
              Cancel
            </Button>
            <Button onClick={saveUser} disabled={submitting} className="rounded-xl bg-indigo-600 hover:bg-indigo-700">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingUser ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagementPage;
